import React, { Component } from 'react';
import { Tab, Nav, Button, Form, Table, Badge, Spinner } from 'react-bootstrap';
import toastr from 'toastr';
import {
    getAllLabHubLists,
    addLabHubItem,
    updateLabHubItem,
    deleteLabHubItem
} from '../../../services/clinicPortalServices/labHubService';

// Map listName → friendly tab label and icon
const LIST_META = {
    specimenTypes: { label: 'Specimen Types', icon: 'fa-vial' },
    symptoms: { label: 'Symptoms', icon: 'fa-notes-medical' },
    qcControlTypes: { label: 'QC Control Types', icon: 'fa-chart-bar' }
};

const TAB_ORDER = ['specimenTypes', 'symptoms', 'qcControlTypes'];

export default class LabHubRecords extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            lists: {}, // { listName: LabHubList doc }
            activeTab: 'specimenTypes',
            // per-tab add-item form state
            newItemValue: '',
            // per-item inline editing
            editingItemId: null,
            editingValue: '',
            // per-item busy state
            savingItemId: null,
            deletingItemId: null,
            addingItem: false
        };
    }

    componentDidMount() {
        this.loadLists();
    }

    loadLists = () => {
        this.setState({ loading: true });
        getAllLabHubLists()
            .then((res) => {
                const lists = {};
                (res.data || []).forEach((doc) => {
                    lists[doc.listName] = doc;
                });
                this.setState({ lists, loading: false });
            })
            .catch(() => {
                toastr.error('Failed to load lookup lists');
                this.setState({ loading: false });
            });
    };

    // ── Add item ──────────────────────────────────────────────
    handleAddItem = (listName) => {
        const value = this.state.newItemValue.trim();
        if (!value) {
            toastr.warning('Please enter a value');
            return;
        }
        this.setState({ addingItem: true });
        addLabHubItem(listName, { value, label: value })
            .then((res) => {
                const lists = { ...this.state.lists, [listName]: res.data };
                this.setState({ lists, newItemValue: '', addingItem: false });
                toastr.success('Item added');
            })
            .catch(() => {
                toastr.error('Failed to add item');
                this.setState({ addingItem: false });
            });
    };

    // ── Inline edit ───────────────────────────────────────────
    startEdit = (item) => {
        this.setState({ editingItemId: item._id, editingValue: item.value });
    };

    cancelEdit = () => {
        this.setState({ editingItemId: null, editingValue: '' });
    };

    saveEdit = (listName, itemId) => {
        const value = this.state.editingValue.trim();
        if (!value) {
            toastr.warning('Value cannot be empty');
            return;
        }
        this.setState({ savingItemId: itemId });
        updateLabHubItem(listName, itemId, { value, label: value })
            .then((res) => {
                const lists = { ...this.state.lists, [listName]: res.data };
                this.setState({ lists, editingItemId: null, editingValue: '', savingItemId: null });
                toastr.success('Item updated');
            })
            .catch(() => {
                toastr.error('Failed to update item');
                this.setState({ savingItemId: null });
            });
    };

    // ── Delete item ───────────────────────────────────────────
    handleDelete = (listName, itemId) => {
        if (!window.confirm('Remove this item?')) return;
        this.setState({ deletingItemId: itemId });
        deleteLabHubItem(listName, itemId)
            .then((res) => {
                const lists = { ...this.state.lists, [listName]: res.data };
                this.setState({ lists, deletingItemId: null });
                toastr.success('Item removed');
            })
            .catch(() => {
                toastr.error('Failed to remove item');
                this.setState({ deletingItemId: null });
            });
    };

    // ── Render a single list tab ──────────────────────────────
    renderListTab(listName) {
        const doc = this.state.lists[listName];
        const { editingItemId, editingValue, savingItemId, deletingItemId, addingItem, newItemValue } = this.state;

        if (!doc) {
            return (
                <div className="text-center py-5 text-muted">
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Loading…
                </div>
            );
        }

        const items = [...(doc.items || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

        return (
            <div>
                {doc.description && (
                    <p className="text-muted small mb-3">
                        <i className="fas fa-info-circle mr-1"></i>
                        {doc.description}
                    </p>
                )}

                {/* Add new item form */}
                <div className="d-flex mb-3" style={{ gap: 8 }}>
                    <Form.Control
                        type="text"
                        placeholder="New item value…"
                        value={this.state.activeTab === listName ? newItemValue : ''}
                        onChange={(e) => this.setState({ newItemValue: e.target.value })}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') this.handleAddItem(listName);
                        }}
                        style={{ maxWidth: 320 }}
                        disabled={addingItem}
                    />
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => this.handleAddItem(listName)}
                        disabled={addingItem}
                    >
                        {addingItem ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <><i className="fas fa-plus mr-1"></i> Add</>
                        )}
                    </Button>
                </div>

                {/* Items table */}
                {items.length === 0 ? (
                    <div className="text-center py-4 text-muted border rounded">
                        <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                        No items yet — add one above.
                    </div>
                ) : (
                    <div className="table-responsive">
                    <Table bordered hover size="sm" className="lab-hub-table">
                        <thead className="thead-light">
                            <tr>
                                <th style={{ width: 40 }}>#</th>
                                <th>Value</th>
                                <th style={{ width: 80 }}>Status</th>
                                <th style={{ width: 140 }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, idx) => {
                                const isEditing = editingItemId === item._id;
                                const isSaving = savingItemId === item._id;
                                const isDeleting = deletingItemId === item._id;
                                return (
                                    <tr key={item._id}>
                                        <td className="text-muted text-center">{idx + 1}</td>
                                        <td>
                                            {isEditing ? (
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={editingValue}
                                                    onChange={(e) => this.setState({ editingValue: e.target.value })}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') this.saveEdit(listName, item._id);
                                                        if (e.key === 'Escape') this.cancelEdit();
                                                    }}
                                                    autoFocus
                                                />
                                            ) : (
                                                <span
                                                    style={{ cursor: 'pointer' }}
                                                    title="Click to edit"
                                                    onDoubleClick={() => this.startEdit(item)}
                                                >
                                                    {item.value}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <Badge variant={item.active ? 'success' : 'secondary'}>
                                                {item.active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {isEditing ? (
                                                <div className="d-flex" style={{ gap: 4 }}>
                                                    <Button
                                                        size="sm"
                                                        variant="success"
                                                        onClick={() => this.saveEdit(listName, item._id)}
                                                        disabled={isSaving}
                                                    >
                                                        {isSaving ? <Spinner animation="border" size="sm" /> : <i className="fas fa-check"></i>}
                                                    </Button>
                                                    <Button size="sm" variant="secondary" onClick={this.cancelEdit}>
                                                        <i className="fas fa-times"></i>
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="d-flex" style={{ gap: 4 }}>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        title="Edit"
                                                        onClick={() => this.startEdit(item)}
                                                    >
                                                        <i className="fas fa-pencil-alt"></i>
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        title="Delete"
                                                        onClick={() => this.handleDelete(listName, item._id)}
                                                        disabled={isDeleting}
                                                    >
                                                        {isDeleting ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <i className="fas fa-trash-alt"></i>
                                                        )}
                                                    </Button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                    </div>
                )}

                <div className="text-muted small mt-2">
                    <i className="fas fa-lightbulb mr-1"></i>
                    Double-click any row to edit inline. Press <kbd>Enter</kbd> to save, <kbd>Esc</kbd> to cancel.
                </div>
            </div>
        );
    }

    render() {
        const { loading, activeTab } = this.state;

        return (
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <h4 className="mb-0">
                                    <i className="fas fa-list-alt mr-2 text-primary"></i>
                                    Lab Hub Record Management
                                </h4>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={this.loadLists}
                                    disabled={loading}
                                    title="Refresh"
                                >
                                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                                </Button>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" />
                                        <p className="mt-2 text-muted">Loading lookup lists…</p>
                                    </div>
                                ) : (
                                    <Tab.Container
                                        activeKey={activeTab}
                                        onSelect={(k) => this.setState({ activeTab: k, newItemValue: '', editingItemId: null })}
                                    >
                                        <Nav variant="tabs" className="mb-3">
                                            {[...TAB_ORDER.filter((n) => this.state.lists[n]), ...Object.keys(this.state.lists).filter((n) => !TAB_ORDER.includes(n)).sort()].map((listName) => {
                                                const meta = LIST_META[listName] || { label: (this.state.lists[listName] && this.state.lists[listName].displayName) || listName, icon: ({ units: 'fa-ruler', race: 'fa-users', ethnicity: 'fa-globe-americas', relation: 'fa-user-friends' })[listName] || 'fa-list' };
                                                const doc = this.state.lists[listName];
                                                const count = doc ? (doc.items || []).length : 0;
                                                return (
                                                    <Nav.Item key={listName}>
                                                        <Nav.Link eventKey={listName}>
                                                            <i className={`fas ${meta.icon} mr-1`}></i>
                                                            {meta.label}
                                                            <Badge
                                                                variant="secondary"
                                                                className="ml-2"
                                                                style={{ fontSize: '0.7rem' }}
                                                            >
                                                                {count}
                                                            </Badge>
                                                        </Nav.Link>
                                                    </Nav.Item>
                                                );
                                            })}
                                        </Nav>
                                        <Tab.Content>
                                            {[...TAB_ORDER.filter((n) => this.state.lists[n]), ...Object.keys(this.state.lists).filter((n) => !TAB_ORDER.includes(n)).sort()].map((listName) => (
                                                <Tab.Pane key={listName} eventKey={listName}>
                                                    {this.renderListTab(listName)}
                                                </Tab.Pane>
                                            ))}
                                        </Tab.Content>
                                    </Tab.Container>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
